/** Contratos del protocolo de sincronizacion bidireccional. */

import { z } from "zod";
import { idSchema, instantSchema, MAX_SYNC_BATCH } from "./common.js";
import {
  chargeSchema,
  conceptSchema,
  cycleSchema,
  gradeSchema,
  institutionSchema,
  sectionSchema,
  studentSchema,
} from "./academic.js";
import {
  createStudentRequestSchema,
  setStudentActiveRequestSchema,
  updateStudentRequestSchema,
} from "./studentsAdmin.js";
import {
  createPaymentRequestSchema,
  paymentSchema,
  voidPaymentRequestSchema,
} from "./payments.js";

export const syncEntitySchema = z.enum([
  "institucion",
  "ciclos",
  "grados",
  "secciones",
  "estudiantes",
  "conceptos",
  "cargos",
  "saldos",
  "pagos",
]);
export type SyncEntity = z.infer<typeof syncEntitySchema>;

// ---------------------------------------------------------------------------
// Salida del dispositivo (push)
// ---------------------------------------------------------------------------

/**
 * Una operacion de la cola de salida. El `type` decide la forma del payload.
 * `idempotencyKey` es obligatoria y es lo que impide pagos duplicados aunque
 * varios reintentos lleguen simultaneamente.
 */
export const syncOperationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("PAGO"),
    operationId: idSchema,
    idempotencyKey: z.string().min(8).max(200),
    createdAt: instantSchema,
    payload: createPaymentRequestSchema,
  }),
  z.object({
    type: z.literal("ANULACION"),
    operationId: idSchema,
    idempotencyKey: z.string().min(8).max(200),
    createdAt: instantSchema,
    payload: voidPaymentRequestSchema.extend({ paymentId: idSchema }),
  }),
  /**
   * Alta de estudiante capturada sin conexion. El identificador lo genera el
   * dispositivo, asi que reenviarla no duplica: el servidor la reconoce.
   */
  z.object({
    type: z.literal("ESTUDIANTE_ALTA"),
    operationId: idSchema,
    idempotencyKey: z.string().min(8).max(200),
    createdAt: instantSchema,
    payload: createStudentRequestSchema,
  }),
  z.object({
    type: z.literal("ESTUDIANTE_EDICION"),
    operationId: idSchema,
    idempotencyKey: z.string().min(8).max(200),
    createdAt: instantSchema,
    payload: updateStudentRequestSchema.extend({ studentId: idSchema }),
  }),
  z.object({
    type: z.literal("ESTUDIANTE_ESTADO"),
    operationId: idSchema,
    idempotencyKey: z.string().min(8).max(200),
    createdAt: instantSchema,
    payload: setStudentActiveRequestSchema.extend({ studentId: idSchema }),
  }),
  z.object({
    type: z.literal("REIMPRESION"),
    operationId: idSchema,
    idempotencyKey: z.string().min(8).max(200),
    createdAt: instantSchema,
    payload: z.object({
      paymentId: idSchema,
      installationId: idSchema,
      at: instantSchema,
      reason: z.string().max(200).nullable().default(null),
    }),
  }),
]);
export type SyncOperation = z.infer<typeof syncOperationSchema>;

export const syncPushRequestSchema = z.object({
  installationId: idSchema,
  operations: z.array(syncOperationSchema).min(1).max(MAX_SYNC_BATCH),
});
export type SyncPushRequest = z.infer<typeof syncPushRequestSchema>;

/**
 * Resultado por operacion. Es lo que la PWA usa para mover cada entrada de su
 * outbox: `ACEPTADO`/`DUPLICADO` -> SINCRONIZADO, `CONFLICTO` -> revision,
 * `RECHAZADO` -> error permanente.
 */
export const syncOperationResultSchema = z.object({
  operationId: idSchema,
  outcome: z.enum(["ACEPTADO", "DUPLICADO", "RECHAZADO", "CONFLICTO"]),
  /** Estado final del pago en el servidor, cuando aplique. */
  payment: paymentSchema.nullable(),
  /** Identificador del estudiante afectado, en las operaciones de estudiante. */
  studentId: idSchema.nullable().default(null),
  conflictId: idSchema.nullable(),
  errorCode: z.string().nullable(),
  /** Mensaje en espanol apto para mostrar al cajero, sin detalles internos. */
  errorMessage: z.string().nullable(),
});
export type SyncOperationResult = z.infer<typeof syncOperationResultSchema>;

export const syncPushResponseSchema = z.object({
  results: z.array(syncOperationResultSchema),
  serverTime: instantSchema,
});
export type SyncPushResponse = z.infer<typeof syncPushResponseSchema>;

// ---------------------------------------------------------------------------
// Entrada al dispositivo (pull incremental)
// ---------------------------------------------------------------------------

export const syncPullQuerySchema = z.object({
  /** Cursor por entidad: `entidad:instante`. Ausente = descarga inicial completa. */
  since: instantSchema.optional(),
  entities: z
    .string()
    .optional()
    .transform((value) => (value ? value.split(",") : undefined))
    .pipe(z.array(syncEntitySchema).optional()),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
  /** Pagina dentro del mismo cursor. El cliente repite mientras `hasMore` sea true. */
  offset: z.coerce.number().int().min(0).default(0),
});

/**
 * Cambios incrementales. Los borrados viajan como tombstones (`deletedIds`), nunca
 * como ausencia: asi el dispositivo sabe que debe eliminar el registro local.
 */
export const syncPullResponseSchema = z.object({
  institution: institutionSchema.nullable(),
  cycles: z.array(cycleSchema),
  grades: z.array(gradeSchema),
  sections: z.array(sectionSchema),
  students: z.array(studentSchema),
  concepts: z.array(conceptSchema),
  charges: z.array(chargeSchema),
  payments: z.array(paymentSchema),
  deletedIds: z.record(syncEntitySchema, z.array(idSchema)),
  /** Cursor a usar en la proxima llamada. */
  cursor: instantSchema,
  /** `true` si quedan mas cambios por descargar (paginar). */
  hasMore: z.boolean(),
  serverTime: instantSchema,
});
export type SyncPullResponse = z.infer<typeof syncPullResponseSchema>;

export const syncStatusSchema = z.object({
  entity: syncEntitySchema,
  lastSyncedAt: instantSchema.nullable(),
  cursor: instantSchema.nullable(),
  /** Antigüedad en segundos; la PWA la muestra junto a cada conjunto de datos. */
  ageSeconds: z.number().int().nullable(),
  stale: z.boolean(),
});
export type SyncStatusDto = z.infer<typeof syncStatusSchema>;
